import clsx from "clsx";
import FormScaffold from "ui/common/components/layout/FormScaffold/FormScaffold";
import { BaseFormScaffoldState } from "ui/common/components/layout/FormScaffold/FormScaffold.types";
import useTranslate from "ui/locale/hooks/useTranslate";
import useGetPartner from "features/partners/hooks/useGetPartner";
import useUpdatePartner from "features/partners/hooks/useUpdatePartner";
import usePointFindBy from "features/points/hooks/usePointFindBy";
import FormGridBanner from "./FormGridBanner/FormGridBanner";
import { PartnerFormGridProps } from "./PartnerFormGrid.types";
import usePartnerAddressAndBackButton from "./hooks/usePartnerAddressAndBackButton";
import usePartnerFormGridRows from "./hooks/usePartnerFormGridRows";
import type { ScheduledRedeemDto } from "common/models/api/models/ScheduledRedeemDto";
import type { UpdatePartnerRequest } from "common/models/api/models/UpdatePartnerRequest";
import { ScheduledRedeemPeriodicity } from "ui/transaction/container/RedeemPointsModal/components/ScheduleRedemption/SchedulingTypeGroup";
import { days } from "ui/common/utils/dateUtils";

function PartnerFormGrid({ className, ...rest }: PartnerFormGridProps) {
    const { partnerAddress, shouldShowBackButton } = usePartnerAddressAndBackButton();
    const { data: partner } = useGetPartner({ address: partnerAddress });
    const { data: point } = usePointFindBy();
    const { isLoading, isSuccess, mutate, reset } = useUpdatePartner();
    const rows = usePartnerFormGridRows();
    const translate = useTranslate();
    const safePartnerAddress = partnerAddress as string;

    function handleOnSubmit(data: BaseFormScaffoldState): void {
        const payload: UpdatePartnerRequest = {
            name: data.name as string,
            description: data.description as string,
            link: data.link as string,
            location: data.location as string,
            logo: data.logo as string,
            banner: data.banner as string,
            type: data.type as string,
        };
        mutate({ ...payload, address: safePartnerAddress });
    }

    function buildScheduledRedeemValue(scheduledRedeem: ScheduledRedeemDto | undefined): string {
        if (!scheduledRedeem) return "";
        if (scheduledRedeem?.frequency === ScheduledRedeemPeriodicity.MONTHLY) {
            return `${translate("monthly")} - ${translate("everyDay")} (${scheduledRedeem?.dayMonth})`;
        } else if (scheduledRedeem?.frequency === ScheduledRedeemPeriodicity.WEEKLY) {
            const day = days.find((item) => item.value === scheduledRedeem?.dayWeek);
            return `${translate("weekly")} - ${translate(day?.localeLabel!)}`; // skipcq: JS-0338
        } else {
            return translate("daily");
        }
    }
    // skipcq: JS-0424
    return (
        <>
            {!partner ? (
                // skipcq: JS-0424
                <></>
            ) : (
                <FormScaffold
                    back={shouldShowBackButton}
                    className={clsx("partner-form-grid", className)}
                    footer={
                        partner.belongsToOtherAlly ? (
                            // skipcq: JS-0424
                            <></>
                        ) : (
                            FormGridBanner
                        )
                    }
                    isLoading={isLoading}
                    isSuccess={isSuccess}
                    link={`/payment-gateway/${partnerAddress}/${point?.address}`}
                    linkText={translate("paymentGateway")}
                    onSubmit={handleOnSubmit}
                    reset={reset}
                    rows={rows}
                    state={{
                        ...partner,
                        scheduledRedeem: {
                            ...partner.scheduledRedeem,
                            frequency: buildScheduledRedeemValue(partner.scheduledRedeem),
                        },
                    }}
                    title={partner?.name}
                    {...rest}
                />
            )}
        </>
    );
}

export default PartnerFormGrid;
